import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STEP_LABELS = {
  0: 'Psychology Map',
  1: 'Angle Selection',
  2: 'Hook Generation',
  3: 'Script Generation',
  4: 'Production',
};

// Which stages can be locked from which step. Step 0 (Psychology Map) and
// step 4 (Production) don't write custom artifacts via the chat-bridge.
const STAGES_BY_STEP = {
  1: ['ANGLE'],
  2: ['HOOK'],
  3: ['SCRIPT', 'CTA'],
};

const STAGE_LABELS = {
  ANGLE: 'Angle',
  HOOK: 'Hook',
  SCRIPT: 'Script',
  CTA: 'CTA',
};

// Enum vocab — kept in sync with backend chat_service.parse_assistant_content_for_stage
const VALID_ENTRY_POINTS = ['FEAR','FRUSTRATION','ASPIRATION','BELIEF','BELONGING','STATUS','CURIOSITY'];
const VALID_AWARENESS = ['UNAWARE','PROBLEM_AWARE','SOLUTION_AWARE','PRODUCT_AWARE','MOST_AWARE'];
const VALID_HOOK_TYPES = ['CURIOSITY_GAP','PATTERN_INTERRUPT','CONTRARIAN','QUESTION_HOOK','STAT_HOOK','PROBLEM_REVEAL','TRANSFORMATION'];
const PLATFORM_OPTS = ['META','TIKTOK','BOTH'];

const isRealAssistantMessage = (msg) => {
  if (!msg || msg.role !== 'assistant') return false;
  const id = msg.message_id || '';
  if (id.startsWith('temp_') || id.startsWith('err_')) return false;
  return true;
};

const ChatMessage = ({ msg, currentStep, onLock }) => {
  const isUser = msg.role === 'user';
  const lockableStages = !isUser && isRealAssistantMessage(msg)
    ? (STAGES_BY_STEP[currentStep] || [])
    : [];

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-3`}>
      <div
        className={`max-w-[85%] rounded-xl px-4 py-3 text-sm ${
          isUser
            ? 'bg-emerald-600/20 text-emerald-100 border border-emerald-500/20'
            : 'bg-slate-800 text-slate-200 border border-slate-700/50'
        }`}
        data-testid={`chat-message-${msg.message_id || msg.role}`}
      >
        <div className="whitespace-pre-wrap leading-relaxed">{msg.content}</div>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-xs opacity-50">
            {msg.step_label || STEP_LABELS[msg.step] || ''}
          </span>
          {msg.model_used && msg.model_used !== 'none' && (
            <span className="text-xs opacity-30">Sonnet</span>
          )}
        </div>
        {lockableStages.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-700/50 flex flex-wrap gap-1.5">
            {lockableStages.map(stage => (
              <button
                key={stage}
                onClick={() => onLock(msg, stage)}
                className="px-2 py-0.5 text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded hover:bg-emerald-500/20 transition-colors"
                data-testid={`chat-lock-${stage.toLowerCase()}-btn-${msg.message_id}`}
                title={`Lock this reply as a new ${STAGE_LABELS[stage]} artifact`}
              >
                🔒 Lock as {STAGE_LABELS[stage]}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// --- Small reusable form-field components used inside LockModal ---

const Field = ({ label, value, onChange, testid }) => (
  <div>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white"
      data-testid={testid}
    />
  </div>
);

const TextareaField = ({ label, value, onChange, testid, rows = 3 }) => (
  <div>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white resize-none"
      data-testid={testid}
    />
  </div>
);

const SelectField = ({ label, value, options, onChange, testid }) => (
  <div>
    <label className="block text-xs text-slate-400 mb-1">{label}</label>
    <select
      value={value || options[0]}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white"
      data-testid={testid}
    >
      {options.map(o => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);

// ============================================================================
// LockModal — Manual Session Mode (Phase 8)
// ----------------------------------------------------------------------------
// Fetches a prefill from /parse-message, lets the operator edit, then POSTs
// to /lock-turn-as-artifact. For HOOK/SCRIPT/CTA the operator also picks
// which existing angle / hook / script the new artifact anchors to.
// ============================================================================

const LockModal = ({ sessionId, offerId, messageId, stage, onClose, onSuccess }) => {
  const [parsed, setParsed] = useState(null);
  const [rawContent, setRawContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Anchor pickers — populated lazily based on stage
  const [angles, setAngles] = useState([]);
  const [hooks, setHooks] = useState([]);
  const [scripts, setScripts] = useState([]);
  const [targetAngleId, setTargetAngleId] = useState('');
  const [targetHookElementId, setTargetHookElementId] = useState('');
  const [targetScriptId, setTargetScriptId] = useState('');

  // Load prefill + anchor options on mount
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const parseRes = await axios.post(
          `${API}/creative/sessions/${sessionId}/parse-message`,
          { message_id: messageId, stage }
        );
        if (!alive) return;
        setParsed(parseRes.data.parsed || {});
        setRawContent(parseRes.data.raw_content || '');

        if (stage !== 'ANGLE') {
          const anglesRes = await axios.get(`${API}/offers/${offerId}/angles`);
          if (!alive) return;
          const angleList = anglesRes.data.angles || [];
          setAngles(angleList);
          const defaultAngle = angleList.find(a => a.selected) || angleList[0];
          if (defaultAngle) setTargetAngleId(defaultAngle.angle_id);
        }
        if (stage === 'SCRIPT') {
          const hooksRes = await axios.get(`${API}/offers/${offerId}/hooks`);
          if (!alive) return;
          setHooks(hooksRes.data.hooks || []);
        }
        if (stage === 'CTA') {
          const scriptsRes = await axios.get(`${API}/offers/${offerId}/scripts`);
          if (!alive) return;
          const scriptList = scriptsRes.data.scripts || [];
          setScripts(scriptList);
          if (scriptList.length > 0) setTargetScriptId(scriptList[0].script_id);
        }
      } catch (err) {
        if (!alive) return;
        setError(err.response?.data?.detail || err.message || 'Failed to load prefill');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [sessionId, messageId, stage, offerId]);

  const updateField = (key, value) => setParsed(prev => ({ ...prev, [key]: value }));
  const updateSectionField = (idx, key, value) => setParsed(prev => {
    const sections = [...(prev.sections || [])];
    sections[idx] = { ...sections[idx], [key]: value };
    return { ...prev, sections };
  });
  const updateCtaVariant = (idx, value) => setParsed(prev => {
    const cta_variants = [...(prev.cta_variants || [])];
    cta_variants[idx] = { ...cta_variants[idx], content: value };
    return { ...prev, cta_variants };
  });

  const validate = () => {
    if (stage === 'ANGLE' && !(parsed?.angle_name || '').trim()) return 'Angle name is required';
    if (stage === 'HOOK' && !(parsed?.content || '').trim()) return 'Hook content is required';
    if (stage === 'HOOK' && !targetAngleId) return 'Pick an angle to anchor this hook to';
    if (stage === 'SCRIPT' && (!parsed?.sections || parsed.sections.length === 0)) return 'Script needs at least one section';
    if (stage === 'SCRIPT' && (!targetAngleId || !targetHookElementId)) return 'Pick an angle and a hook to anchor this script to';
    if (stage === 'CTA' && !(parsed?.content || '').trim()) return 'CTA content is required';
    if (stage === 'CTA' && !targetScriptId) return 'Pick a script to attach this CTA to';
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        message_id: messageId,
        stage,
        extracted_content: parsed,
      };
      if (targetAngleId) payload.target_angle_id = targetAngleId;
      if (targetHookElementId) payload.target_hook_element_id = targetHookElementId;
      if (targetScriptId) payload.target_script_id = targetScriptId;

      const res = await axios.post(
        `${API}/creative/sessions/${sessionId}/lock-turn-as-artifact`,
        payload
      );
      onSuccess(res.data);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Lock failed');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center" data-testid="lock-modal-loading">
        <div className="bg-slate-900 rounded-xl border border-slate-700 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500 mx-auto"></div>
          <p className="text-slate-400 text-sm mt-3">Parsing chat reply…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="lock-modal">
      <div className="bg-slate-900 rounded-xl border border-slate-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h3 className="text-lg font-semibold text-white" data-testid="lock-modal-title">
              Lock as {STAGE_LABELS[stage]}
            </h3>
            <p className="text-slate-500 text-xs mt-0.5">
              Auto-parsed from this chat turn — edit anything before submitting.
            </p>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
            data-testid="lock-modal-close-btn"
          >
            Cancel
          </button>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-3 py-2 rounded text-sm" data-testid="lock-modal-error">
              {error}
            </div>
          )}

          <details className="bg-slate-950/50 rounded-lg border border-slate-800">
            <summary className="cursor-pointer text-xs text-slate-500 px-3 py-2 hover:text-slate-300">
              View original chat reply
            </summary>
            <pre className="px-3 pb-3 text-xs text-slate-400 whitespace-pre-wrap font-mono max-h-48 overflow-y-auto">
              {rawContent}
            </pre>
          </details>

          {/* Anchor pickers */}
          {(stage === 'HOOK' || stage === 'SCRIPT') && (
            <div>
              <label className="block text-xs text-slate-400 mb-1">Anchor to angle</label>
              <select
                value={targetAngleId}
                onChange={(e) => setTargetAngleId(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white"
                data-testid="lock-modal-anchor-angle"
              >
                <option value="">— Pick an angle —</option>
                {angles.map(a => (
                  <option key={a.angle_id} value={a.angle_id}>
                    {a.angle_name} {a.selected ? '✓' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
          {stage === 'SCRIPT' && (
            <div>
              <label className="block text-xs text-slate-400 mb-1">Anchor to hook</label>
              <select
                value={targetHookElementId}
                onChange={(e) => setTargetHookElementId(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white"
                data-testid="lock-modal-anchor-hook"
              >
                <option value="">— Pick a hook —</option>
                {hooks.map(h => (
                  <option key={h.element_id} value={h.element_id}>
                    {(h.content || '').slice(0, 80)}
                  </option>
                ))}
              </select>
            </div>
          )}
          {stage === 'CTA' && (
            <div>
              <label className="block text-xs text-slate-400 mb-1">Attach to script</label>
              <select
                value={targetScriptId}
                onChange={(e) => setTargetScriptId(e.target.value)}
                className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white"
                data-testid="lock-modal-anchor-script"
              >
                <option value="">— Pick a script —</option>
                {scripts.map(s => (
                  <option key={s.script_id} value={s.script_id}>
                    {s.script_name || s.script_id}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Stage-specific form */}
          {stage === 'ANGLE' && parsed && (
            <>
              <Field label="Angle name" value={parsed.angle_name} onChange={v => updateField('angle_name', v)} testid="lock-modal-angle-name" />
              <TextareaField label="Core belief shift" value={parsed.core_belief_shift} onChange={v => updateField('core_belief_shift', v)} testid="lock-modal-angle-belief" />
              <SelectField label="Entry point" value={parsed.psychological_entry_point} options={VALID_ENTRY_POINTS} onChange={v => updateField('psychological_entry_point', v)} testid="lock-modal-angle-entry" />
              <SelectField label="Awareness stage" value={parsed.target_awareness_stage} options={VALID_AWARENESS} onChange={v => updateField('target_awareness_stage', v)} testid="lock-modal-angle-awareness" />
              <Field label="Target persona" value={parsed.target_persona} onChange={v => updateField('target_persona', v)} testid="lock-modal-angle-persona" />
              <TextareaField label="Rationale" value={parsed.angle_rationale} onChange={v => updateField('angle_rationale', v)} testid="lock-modal-angle-rationale" />
              <Field label="Risk" value={parsed.angle_risk} onChange={v => updateField('angle_risk', v)} testid="lock-modal-angle-risk" />
            </>
          )}
          {stage === 'HOOK' && parsed && (
            <>
              <TextareaField label="Hook content" value={parsed.content} onChange={v => updateField('content', v)} testid="lock-modal-hook-content" />
              <SelectField label="Hook type" value={parsed.hook_type} options={VALID_HOOK_TYPES} onChange={v => updateField('hook_type', v)} testid="lock-modal-hook-type" />
              <SelectField label="Platform" value={parsed.platform_optimisation} options={PLATFORM_OPTS} onChange={v => updateField('platform_optimisation', v)} testid="lock-modal-hook-platform" />
              <TextareaField label="Rationale" value={parsed.hook_rationale} onChange={v => updateField('hook_rationale', v)} testid="lock-modal-hook-rationale" />
              <Field label="Angle anchor (text label)" value={parsed.angle_anchor} onChange={v => updateField('angle_anchor', v)} testid="lock-modal-hook-anchor-text" />
            </>
          )}
          {stage === 'SCRIPT' && parsed && (
            <>
              <SelectField label="Platform" value={parsed.platform} options={['META','TIKTOK']} onChange={v => updateField('platform', v)} testid="lock-modal-script-platform" />
              <div>
                <label className="block text-xs text-slate-400 mb-1">Total duration (seconds)</label>
                <input
                  type="number"
                  value={parsed.total_duration_seconds || 45}
                  onChange={(e) => updateField('total_duration_seconds', parseInt(e.target.value, 10) || 45)}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white"
                  data-testid="lock-modal-script-duration"
                />
              </div>
              <div>
                <h4 className="text-sm font-medium text-white mb-2">
                  Sections ({(parsed.sections || []).length})
                </h4>
                <div className="space-y-3">
                  {(parsed.sections || []).map((sec, i) => (
                    <div key={i} className="bg-slate-800/50 rounded-lg p-3 space-y-2" data-testid={`lock-modal-script-section-${i}`}>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={sec.section || ''}
                          onChange={(e) => updateSectionField(i, 'section', e.target.value)}
                          placeholder="Section name"
                          className="w-32 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white font-mono"
                        />
                        <input
                          type="text"
                          value={sec.timing || ''}
                          onChange={(e) => updateSectionField(i, 'timing', e.target.value)}
                          placeholder="0-3s"
                          className="w-20 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-300 font-mono"
                        />
                      </div>
                      <textarea
                        value={sec.spoken_text || ''}
                        onChange={(e) => updateSectionField(i, 'spoken_text', e.target.value)}
                        placeholder="Spoken text"
                        rows={2}
                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white resize-none"
                      />
                      <textarea
                        value={sec.visual_direction || ''}
                        onChange={(e) => updateSectionField(i, 'visual_direction', e.target.value)}
                        placeholder="Visual direction"
                        rows={1}
                        className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-400 resize-none"
                      />
                    </div>
                  ))}
                </div>
              </div>
              {(parsed.cta_variants || []).length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-white mb-2">CTA variants</h4>
                  <div className="space-y-2">
                    {parsed.cta_variants.map((cta, i) => (
                      <input
                        key={i}
                        type="text"
                        value={cta.content || ''}
                        onChange={(e) => updateCtaVariant(i, e.target.value)}
                        className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white"
                        data-testid={`lock-modal-script-cta-${i}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
          {stage === 'CTA' && parsed && (
            <>
              <TextareaField label="CTA content" value={parsed.content} onChange={v => updateField('content', v)} testid="lock-modal-cta-content" />
              <TextareaField label="Rationale" value={parsed.rationale} onChange={v => updateField('rationale', v)} testid="lock-modal-cta-rationale" />
            </>
          )}
        </div>

        <div className="sticky bottom-0 bg-slate-900 border-t border-slate-800 px-6 py-4 flex justify-end gap-2 z-10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-slate-700 text-slate-300 rounded-lg hover:bg-slate-600"
            data-testid="lock-modal-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="lock-modal-submit-btn"
          >
            {submitting ? 'Locking…' : `🔒 Lock as ${STAGE_LABELS[stage]}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export const ChatPanel = ({ sessionId, currentStep, offer, getStepContent, onLockSuccess }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [transcriptSaved, setTranscriptSaved] = useState(false);
  const [lockModal, setLockModal] = useState(null);  // { messageId, stage } | null
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Save transcript on unmount (session end)
  useEffect(() => {
    return () => {
      if (sessionId && messages.length > 0 && !transcriptSaved) {
        axios.post(`${API}/creative/sessions/${sessionId}/end`).catch(() => {});
      }
    };
  }, [sessionId, messages.length, transcriptSaved]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const loadPreviousMessages = async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/creative/sessions/${sessionId}/messages`);
      setMessages(res.data.messages || []);
    } catch (err) {
      console.error('Failed to load chat history:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEndSession = async () => {
    if (!sessionId || messages.length === 0) return;
    try {
      await axios.post(`${API}/creative/sessions/${sessionId}/end`);
      setTranscriptSaved(true);
    } catch (err) {
      console.error('Failed to save transcript:', err);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || sending || !sessionId) return;

    const userMessage = input.trim();
    setInput('');
    setSending(true);

    const tempUserMsg = {
      message_id: `temp_${Date.now()}`,
      role: 'user',
      content: userMessage,
      step: currentStep,
      step_label: STEP_LABELS[currentStep],
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      const stepContent = getStepContent ? getStepContent() : {};

      const res = await axios.post(`${API}/creative/sessions/${sessionId}/chat`, {
        message: userMessage,
        step: currentStep,
        step_content: stepContent,
      });

      setMessages(prev => [
        ...prev.filter(m => m.message_id !== tempUserMsg.message_id),
        res.data.user_message,
        res.data.assistant_message,
      ]);
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [
        ...prev,
        {
          message_id: `err_${Date.now()}`,
          role: 'assistant',
          content: 'Sorry, something went wrong. Please try again.',
          step: currentStep,
        },
      ]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleOpenLock = (msg, stage) => {
    setLockModal({ messageId: msg.message_id, stage });
  };

  const handleLockSuccess = (lockResponse) => {
    if (onLockSuccess) onLockSuccess(lockResponse);
  };

  if (!sessionId) {
    return null;
  }

  return (
    <>
      <div
        className={`bg-slate-900/70 border border-slate-800 rounded-xl flex flex-col transition-all ${
          collapsed ? 'h-12' : 'h-[500px]'
        }`}
        data-testid="chat-panel"
      >
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center justify-between px-4 py-3 border-b border-slate-800 cursor-pointer hover:bg-slate-800/30 transition-colors flex-shrink-0"
          data-testid="chat-panel-toggle"
        >
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <span className="text-sm font-medium text-white">Creative Agent</span>
            <span className="text-xs text-slate-500">{messages.length} msgs</span>
            {messages.length > 0 && !transcriptSaved && (
              <button
                onClick={(e) => { e.stopPropagation(); handleEndSession(); }}
                className="ml-2 px-2 py-0.5 text-xs bg-slate-700 text-slate-400 rounded hover:bg-slate-600 hover:text-white transition-colors"
                data-testid="save-transcript-btn"
              >
                Save Transcript
              </button>
            )}
            {transcriptSaved && (
              <span className="ml-2 text-xs text-emerald-400">Saved</span>
            )}
          </div>
          <svg className={`w-4 h-4 text-slate-400 transition-transform ${collapsed ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {!collapsed && (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1 min-h-0" data-testid="chat-messages-container">
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-emerald-500"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <p className="text-slate-500 text-sm">Chat with the creative agent</p>
                  <p className="text-slate-600 text-xs mt-1 max-w-xs">
                    Provide direction, ask for changes, or guide the creative process at any step
                  </p>
                  <button
                    onClick={loadPreviousMessages}
                    className="mt-3 px-3 py-1.5 text-xs bg-slate-800 text-slate-400 rounded-lg hover:bg-slate-700 hover:text-slate-300 border border-slate-700 transition-colors"
                    data-testid="load-transcript-btn"
                  >
                    Load Previous Messages
                  </button>
                </div>
              ) : (
                messages.map((msg, idx) => (
                  <ChatMessage
                    key={msg.message_id || idx}
                    msg={msg}
                    currentStep={currentStep}
                    onLock={handleOpenLock}
                  />
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="border-t border-slate-800 p-3 flex-shrink-0">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={`Message about ${STEP_LABELS[currentStep] || 'this step'}...`}
                  rows={1}
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 resize-none focus:outline-none focus:border-emerald-500 max-h-24"
                  disabled={sending}
                  data-testid="chat-input"
                />
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || sending}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  data-testid="chat-send-btn"
                >
                  {sending ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    'Send'
                  )}
                </button>
              </div>
              <p className="text-xs text-slate-600 mt-1">
                Enter to send, Shift+Enter for new line
              </p>
            </div>
          </>
        )}
      </div>

      {lockModal && (
        <LockModal
          sessionId={sessionId}
          offerId={offer?.offer_id}
          messageId={lockModal.messageId}
          stage={lockModal.stage}
          onClose={() => setLockModal(null)}
          onSuccess={handleLockSuccess}
        />
      )}
    </>
  );
};

export default ChatPanel;
